import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './YfDataStatus.css';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../components/foundations/Icon';
import Tooltip from '../../../components/molecules/Tooltip';
import MetricCell from './MetricCell';
import useElapsed from '../../hooks/useElapsed';
import {
    accountSide,
    AUDIT_METRICS,
    AUDIT_STATUS,
    formatCheckTime,
    formatDelta,
    formatElapsed,
    formatMetricValue,
    hasDeltaChip,
    metricDisplayValue,
    hasAdnetSide,
    latestLoad,
    rowGroup,
    SLOW_PULL_MS,
    yfTranslationKey,
} from '../../yfDataStatusUtils';
import { auditAccountShape, auditDeltaShape, auditRowShape } from './propTypes';

function ConnectorMark({ connector }) {
    if (connector.logo_path) {
        return <img className={css.logo} src={connector.logo_path} alt="" />;
    }
    return <span className={css.logoStub}>{(connector.display_name || '?').charAt(0)}</span>;
}

ConnectorMark.propTypes = {
    connector: auditRowShape.isRequired,
};

function LoadedBy({ pullTag, loadedAt, translate }) {
    return (
        <>
            {pullTag ? (
                <span className={css.loadTooltipTag}>{pullTag}</span>
            ) : (
                translate(yfTranslationKey('LOADED_BY_MANUAL'))
            )}
            {loadedAt &&
                translate(yfTranslationKey('LOADED_AT_TOOLTIP'), { loadedAt: formatCheckTime(loadedAt, 'UTC') })}
        </>
    );
}

LoadedBy.propTypes = {
    pullTag: PropTypes.string,
    loadedAt: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

function LoadTooltip({ connector, translate }) {
    const accountLoads = (connector.loads || []).filter(load => load.account);

    if (accountLoads.length) {
        return (
            <>
                <div>{translate(yfTranslationKey('LOADED_BY_ACCOUNT_TOOLTIP'))}</div>
                <ul className={css.loadTooltipAccounts}>
                    {accountLoads.map((load, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                            <strong>{load.account}</strong>:{' '}
                            {load.pull_tag || load.loaded_at ? (
                                <LoadedBy pullTag={load.pull_tag} loadedAt={load.loaded_at} translate={translate} />
                            ) : (
                                translate(yfTranslationKey('ACCOUNT_NOT_LOADED'))
                            )}
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    const load = latestLoad(connector);
    if (!load.pull_tag) {
        return translate(yfTranslationKey('LOADED_BY_MANUAL_TOOLTIP'));
    }

    return (
        <>
            {translate(yfTranslationKey('LOADED_BY_TOOLTIP'))}{' '}
            <LoadedBy pullTag={load.pull_tag} loadedAt={load.loaded_at} translate={translate} />
        </>
    );
}

LoadTooltip.propTypes = {
    connector: auditRowShape.isRequired,
    translate: PropTypes.func.isRequired,
};

function AccountMetric({ delta, comparable, translate }) {
    const { label, tone } = formatDelta(delta);
    const differs = comparable && hasDeltaChip(delta);

    return (
        <div className={css.accountMetric}>
            <div className={css.accountMetricLine}>
                {differs && <span className={`${css.chip} ${css[`chip_${tone}`]}`}>{label}</span>}
                <span className={css.metricValue}>{metricDisplayValue(delta)}</span>
            </div>
            {differs && delta.adnet_value !== null && delta.adnet_value !== undefined && (
                <div className={css.accountAdnetValue}>
                    {translate(yfTranslationKey('ADNET_VALUE_INLINE'), { value: formatMetricValue(delta.adnet_value) })}
                </div>
            )}
        </div>
    );
}

AccountMetric.propTypes = {
    delta: auditDeltaShape.isRequired,
    comparable: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
};

const accountLoadShape = PropTypes.shape({
    pull_tag: PropTypes.string,
    loaded_at: PropTypes.string,
});

function AccountLoad({ load = null, translate }) {
    if (!load) {
        return null;
    }
    if (!load.loaded_at) {
        return <span className={css.loadManual}>{translate(yfTranslationKey('ACCOUNT_NOT_LOADED'))}</span>;
    }
    return (
        <>
            <div className={css.loadTag}>{formatCheckTime(load.loaded_at)}</div>
            <div className={css.accountLoadTask} title={load.pull_tag}>
                {load.pull_tag || translate(yfTranslationKey('LOADED_BY_MANUAL'))}
            </div>
        </>
    );
}

AccountLoad.propTypes = {
    load: accountLoadShape,
    translate: PropTypes.func.isRequired,
};

function AccountRow({ account, load = null, comparable, translate }) {
    const label = account.name || account.account_id || translate(yfTranslationKey('NO_ACCOUNT_ID'));
    const side = accountSide(account.deltas);

    return (
        <div className={`${css.row} ${css.accountRow}`}>
            <div className={css.accountNameCell}>
                <div className={css.accountName} title={label}>
                    {label}
                </div>
                {account.name && (
                    <div className={css.subLabel} title={account.account_id}>
                        {account.account_id}
                    </div>
                )}
                {comparable && side && (
                    <span className={css.accountSideTag}>{translate(yfTranslationKey(`ACCOUNT_SIDE.${side}`))}</span>
                )}
            </div>
            {AUDIT_METRICS.map(metric => (
                <AccountMetric
                    key={metric}
                    delta={account.deltas[metric]}
                    comparable={comparable}
                    translate={translate}
                />
            ))}
            <div className={css.loadCell}>
                <AccountLoad load={load} translate={translate} />
            </div>
        </div>
    );
}

AccountRow.propTypes = {
    account: auditAccountShape.isRequired,
    load: accountLoadShape,
    comparable: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
};

function ConnectorRow({
    connector,
    isCalculating,
    isQueued = false,
    startedAt = null,
    error = null,
    onCalculate,
    translate,
}) {
    const elapsedMs = useElapsed(isCalculating ? startedAt : null);
    const [showAccounts, setShowAccounts] = useState(false);

    const group = rowGroup(connector);
    const isBusy = isCalculating || isQueued;
    const hasError = !!error && !isCalculating;
    const comparable = hasAdnetSide(connector.deltas);
    const lastLoad = latestLoad(connector);
    const isVerifiedEmpty = !comparable && connector.status === AUDIT_STATUS.SYNCED;
    const isUnavailable = connector.status === AUDIT_STATUS.UNAVAILABLE;
    const accounts = connector.accounts || [];

    return (
        <>
            <div className={`${css.row} ${css[`edge_${group}`]} ${isBusy ? css.rowBusy : ''}`}>
                <div className={css.nameCell}>
                    <ConnectorMark connector={connector} />
                    <div className={css.nameText}>
                        <div className={css.connectorName} title={connector.display_name}>
                            {connector.display_name}
                        </div>
                        <div className={css.subLabel} title={connector.label}>
                            {connector.label}
                        </div>
                        {connector.audit_uan_id && (
                            <div className={css.subLabel}>
                                {translate(yfTranslationKey('AUDIT_UAN'), { id: connector.audit_uan_id })}
                            </div>
                        )}
                        {accounts.length > 0 && (
                            <button
                                type="button"
                                className={css.accountsToggle}
                                aria-expanded={showAccounts}
                                onClick={() => setShowAccounts(shown => !shown)}
                            >
                                <Icon
                                    name={showAccounts ? 'chevron-down' : 'chevron-right'}
                                    size={IconSizes.XX_SMALL}
                                    interactive={false}
                                />
                                {translate(yfTranslationKey('ACCOUNTS_TOGGLE'), { count: accounts.length })}
                            </button>
                        )}
                    </div>
                </div>

                {hasError && (
                    <div className={css.spanningCell} title={error}>
                        <Icon
                            name="alert-triangle"
                            type={IconTypes.TABLER}
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.ERROR}
                            interactive={false}
                        />
                        <span className={`${css.spanningText} ${css.spanningError}`}>{error}</span>
                    </div>
                )}

                {!hasError && isUnavailable && (
                    <div className={css.spanningCell}>
                        <Icon
                            name="alert-triangle"
                            type={IconTypes.TABLER}
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.WARNING}
                            interactive={false}
                        />
                        <span className={`${css.spanningText} ${css.spanningWarning}`}>
                            {translate(yfTranslationKey('UNAVAILABLE_EXPLANATION'))}
                        </span>
                    </div>
                )}

                {!hasError && isVerifiedEmpty && (
                    <div className={css.spanningCell}>
                        <span className={css.spanningText}>{translate(yfTranslationKey('EMPTY_DAY_EXPLANATION'))}</span>
                    </div>
                )}

                {!hasError &&
                    !isVerifiedEmpty &&
                    !isUnavailable &&
                    AUDIT_METRICS.map(metric => (
                        <MetricCell
                            key={metric}
                            delta={connector.deltas[metric]}
                            comparable={comparable}
                            translate={translate}
                        />
                    ))}

                <div className={css.loadCell}>
                    <Tooltip
                        title={<LoadTooltip connector={connector} translate={translate} />}
                        wrapperClass={css.loadTooltip}
                    >
                        <span className={lastLoad.loaded_at ? css.loadTag : css.loadManual}>
                            {formatCheckTime(lastLoad.loaded_at) || translate(yfTranslationKey('LOADED_BY_MANUAL'))}
                        </span>
                    </Tooltip>
                </div>

                <div className={css.checkCell}>
                    {isBusy ? (
                        <span className={css.checking}>
                            <span className={css.checkingSpinner} />
                            {isCalculating ? formatElapsed(elapsedMs) : translate(yfTranslationKey('STATUSES.QUEUED'))}
                        </span>
                    ) : (
                        <>
                            <span className={css.checkTime}>{formatCheckTime(connector.pulled_at)}</span>
                            {connector.can_calculate && (
                                <button
                                    type="button"
                                    className={css.checkButton}
                                    title={translate(yfTranslationKey('CALCULATE_ROW_TOOLTIP'))}
                                    aria-label={translate(yfTranslationKey('CALCULATE_ROW_BUTTON'))}
                                    onClick={() => onCalculate(connector.uan_id, true)}
                                >
                                    <Icon
                                        name="refresh"
                                        type={IconTypes.TABLER}
                                        size={IconSizes.XX_SMALL}
                                        variant={IconVariants.ACTIVE}
                                    />
                                </button>
                            )}
                        </>
                    )}
                </div>

                {elapsedMs > SLOW_PULL_MS && (
                    <div className={css.rowHint}>{translate(yfTranslationKey('SLOW_PULL_HINT'))}</div>
                )}
            </div>
            {showAccounts &&
                accounts.map(account => (
                    <AccountRow
                        key={account.account_id}
                        account={account}
                        load={(connector.loads || []).find(entry => entry.account_id === account.account_id)}
                        comparable={comparable}
                        translate={translate}
                    />
                ))}
        </>
    );
}

ConnectorRow.propTypes = {
    connector: auditRowShape.isRequired,
    isCalculating: PropTypes.bool.isRequired,
    isQueued: PropTypes.bool,
    startedAt: PropTypes.number,
    error: PropTypes.string,
    onCalculate: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default ConnectorRow;
